import { useContext, useState, useEffect } from "react";
import classNames from "classnames";

import { MapContext } from "../../../../Map";
import KeyboardButton from "../KeyboardButton/KeyboardButton";
import PinyinSuggestions from "../ZhHKKeyboard/PinyinSuggestions";
import { PinyinService } from "../../../../../../services/pinyinService";
import { KeyboardType } from "../../Keyboard";

import styles from "./ZhCNKeyboard.module.scss";

interface Props {
  handleClick: (...args: any[]) => void;
  handleBackspace: (...args: any[]) => void;
  handleKeyboardTypeSwitch?: (type: KeyboardType) => void;
  handleSearch: () => void;
  handleReplace?: (oldText: string, newText: string) => void;
  languageSwitchButton?: React.ReactNode;
  showSearchKey?: boolean;
}

const ZhCNKeyboard: React.FC<Props> = ({
  handleClick,
  handleBackspace,
  handleKeyboardTypeSwitch,
  handleSearch,
  handleReplace,
  languageSwitchButton,
  showSearchKey = true,
}) => {
  const { controlTheme } = useContext(MapContext);
  const [compositionState, setCompositionState] = useState("");
  const [candidates, setCandidates] = useState<string[]>([]);

  // Handle character input and generate suggestions
  const handlePinyinClick = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    const char = e.currentTarget.name;
    const newComposition = compositionState + char;
    setCompositionState(newComposition);

    // Generate candidates for the current composition
    const newCandidates = PinyinService.getCandidates(newComposition);
    setCandidates(newCandidates);

    // Add to parent input field for immediate visual feedback
    handleClick(e);
  };

  // Handle candidate selection
  const handleCandidateSelect = (candidate: string) => {
    if (handleReplace && compositionState.length > 0) {
      handleReplace(compositionState, candidate);
    } else {
      // Fallback: just add the Chinese characters directly
      addChineseCharacters(candidate);
    }

    // Clear composition state
    setCompositionState("");
    setCandidates([]);
  };

  // Helper function to add Chinese characters one by one with delay
  const addChineseCharacters = (text: string) => {
    const chars = Array.from(text);
    let charIndex = 0;

    const addNextChar = () => {
      if (charIndex < chars.length) {
        handleClick({ currentTarget: { name: chars[charIndex] } });
        charIndex++;
        setTimeout(addNextChar, 100);
      }
    };

    addNextChar();
  };

  // Handle space key - should clear composition and add space
  const handleSpaceKey = () => {
    // If we have active composition, clear it first
    if (compositionState.length > 0) {
      setCompositionState("");
      setCandidates([]);
    }

    // Add space to input
    handleClick({ currentTarget: { name: " " } });
  };

  // Handle backspace in composition mode
  const handleCompositionBackspace = () => {
    // Always call parent backspace to remove character from input field
    handleBackspace();

    // Update composition state if we have active composition
    if (compositionState.length > 0) {
      const newComposition = compositionState.slice(0, -1);
      setCompositionState(newComposition);

      if (newComposition.length > 0) {
        const newCandidates = PinyinService.getCandidates(newComposition);
        setCandidates(newCandidates);
      } else {
        // Clear candidates when composition becomes empty
        setCandidates([]);
      }
    }
  };

  // Reset composition when component unmounts or keyboard changes
  useEffect(() => {
    return () => {
      setCompositionState("");
      setCandidates([]);
    };
  }, []);

  return (
    <div className={classNames(styles.container, styles[controlTheme])}>
      <div className={styles.backdrop} />

      {/* Pinyin Suggestions */}
      <PinyinSuggestions
        candidates={candidates}
        onCandidateSelect={handleCandidateSelect}
        dataQA="zh-cn-suggestions"
      />

      {/* QWERTY Layout - Row 1 */}
      <div className={classNames(styles.row, styles.rowOne)}>
        <KeyboardButton type="text" text="q" onClick={handlePinyinClick} />
        <KeyboardButton type="text" text="w" onClick={handlePinyinClick} />
        <KeyboardButton type="text" text="e" onClick={handlePinyinClick} />
        <KeyboardButton type="text" text="r" onClick={handlePinyinClick} />
        <KeyboardButton type="text" text="t" onClick={handlePinyinClick} />
        <KeyboardButton type="text" text="y" onClick={handlePinyinClick} />
        <KeyboardButton type="text" text="u" onClick={handlePinyinClick} />
        <KeyboardButton type="text" text="i" onClick={handlePinyinClick} />
        <KeyboardButton type="text" text="o" onClick={handlePinyinClick} />
        <KeyboardButton type="text" text="p" onClick={handlePinyinClick} />
      </div>

      {/* QWERTY Layout - Row 2 */}
      <div className={classNames(styles.row, styles.rowTwo)}>
        <KeyboardButton type="text" text="a" onClick={handlePinyinClick} />
        <KeyboardButton type="text" text="s" onClick={handlePinyinClick} />
        <KeyboardButton type="text" text="d" onClick={handlePinyinClick} />
        <KeyboardButton type="text" text="f" onClick={handlePinyinClick} />
        <KeyboardButton type="text" text="g" onClick={handlePinyinClick} />
        <KeyboardButton type="text" text="h" onClick={handlePinyinClick} />
        <KeyboardButton type="text" text="j" onClick={handlePinyinClick} />
        <KeyboardButton type="text" text="k" onClick={handlePinyinClick} />
        <KeyboardButton type="text" text="l" onClick={handlePinyinClick} />
      </div>

      {/* QWERTY Layout - Row 3 */}
      <div className={classNames(styles.row, styles.rowThree)}>
        <div className={styles.rowThreeKeys}>
          <KeyboardButton type="text" text="z" onClick={handlePinyinClick} />
          <KeyboardButton type="text" text="x" onClick={handlePinyinClick} />
          <KeyboardButton type="text" text="c" onClick={handlePinyinClick} />
          <KeyboardButton type="text" text="v" onClick={handlePinyinClick} />
          <KeyboardButton type="text" text="b" onClick={handlePinyinClick} />
          <KeyboardButton type="text" text="n" onClick={handlePinyinClick} />
          <KeyboardButton type="text" text="m" onClick={handlePinyinClick} />
        </div>
        <KeyboardButton
          dataQA="backspace"
          type="icon"
          icon="backspace"
          value=""
          onClick={handleCompositionBackspace}
          className={styles.backspaceKey}
        />
      </div>

      {/* Bottom Row - Controls */}
      <div className={classNames(styles.row, styles.rowFour)}>
        <div className={classNames(styles.column, styles.columnOne)}>
          <KeyboardButton
            dataQA="numeric-switch"
            type="text"
            text="123"
            onClick={
              handleKeyboardTypeSwitch
                ? () => handleKeyboardTypeSwitch("numeric")
                : handleClick
            }
            className={styles.numericSwitchKey}
          />
          {languageSwitchButton && languageSwitchButton}
        </div>
        <div className={classNames(styles.column, styles.columnTwo)}>
          <KeyboardButton
            dataQA="space"
            type="text"
            text=""
            onClick={handleSpaceKey}
            className={styles.spaceKey}
            verticalAlign={false}
          />
          <KeyboardButton
            dataQA="search"
            type="icon"
            icon="search"
            value=""
            onClick={showSearchKey ? handleSearch : () => {}}
            className={classNames(styles.searchKey, {
              [styles.searchKeyHidden]: !showSearchKey,
            })}
            disabled={!showSearchKey}
          />
        </div>
      </div>
    </div>
  );
};

export default ZhCNKeyboard;
